import { DateFormatter } from '@components'

import { amountToDisplayValue, PaymentPane, PaymentPaneProps, Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { billBalanceModifierReferenceToTKey } from '@features/bills/consts/billBalanceModifierReferenceToTKey'
import { BillType } from '@features/bills/enums/billType'
import { BillBalanceModifier } from '@features/bills/types/BillBalanceModifier'
import { getBalanceModifierReference } from '@features/bills/utils/getBalanceModifierReference'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useEmberRouteToUrlGenerator } from '../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { isEmberRoute } from '../../../../../../utils/routing/isEmberRoute'
import { billBalanceModifierReferenceToIconName } from '../../constants/billBalanceModifierReferenceToIconName'
import { creditNoteBalanceModifierReferenceToTKey } from '../../constants/creditNoteBalanceModifierReferenceToKey'
import { getBillPaymentPaneItemRoute } from '../../utils/getBillPaymentPaneItemRoute'

type BillPaymentPaneItemProps = BillBalanceModifier &
  Pick<PaymentPaneProps, 'withHoverEffect'> & {
    billType: BillType | null
  }

export const BillPaymentPaneItem = ({
  amount,
  entryDate,
  modifierReference,
  withHoverEffect,
  billType,
}: BillPaymentPaneItemProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { organization } = useUserOrganization()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const modifierData = useMemo(() => getBalanceModifierReference(modifierReference), [modifierReference])

  const { modifierType, modifierId } = modifierData || {}

  const contentText = useMemo(() => {
    if (modifierType) {
      if (billType === BillType.CreditNote) {
        return t(creditNoteBalanceModifierReferenceToTKey[modifierType])
      }
      return t(billBalanceModifierReferenceToTKey[modifierType])
    }
  }, [billType, modifierType, t])

  const icon = useMemo(() => {
    if (modifierType) {
      return billBalanceModifierReferenceToIconName[modifierType]
    }
  }, [modifierType])

  const handleClick = useCallback(() => {
    if (modifierType && modifierId) {
      const route = getBillPaymentPaneItemRoute(modifierType, modifierId)

      if (isEmberRoute(route)) {
        if (generateUrl) {
          const url = generateUrl(route, modifierId)
          navigate(url)
        }
      } else {
        navigate(`${getOrganizationViewUrl(organization?.url, route)}${location.search}`)
      }
    }
  }, [modifierType, modifierId, generateUrl, navigate, organization?.url, location.search])

  return (
    <PaymentPane.Item
      amount={amount !== null && <Text>{amountToDisplayValue(amount)}</Text>}
      content={
        <Text variant="bodySM" colorVariant="secondary">
          {contentText}
        </Text>
      }
      extra={
        <Text colorVariant="secondary" variant="bodySM">
          {entryDate && <DateFormatter value={entryDate} />}
        </Text>
      }
      icon={icon}
      onClick={handleClick}
      prefix={
        <Text colorVariant="success" variant="bodySM">
          {t('bills.view.payment_pane.item.prefix')}
        </Text>
      }
      withHoverEffect={withHoverEffect}
    />
  )
}
